import type { ResultOf } from '@graphql-typed-document-node/core'
import { json } from '@remix-run/node'
import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { QueryClient, dehydrate } from '@tanstack/query-core'
import { HydrationBoundary } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Coupon } from '~/features/coupon/Coupon.frag'
import { discountCouponsQueryKey } from '~/features/coupon/query'
import { KiwaminoArticleInfo, useInitRestaurantId } from '~/features/guide/KiwaminoArticles'
import { GetRestaurantArticle } from '~/features/guide/getRestaurantArticle.graphql'
import {
  GetRestaurantSummary,
  type RestaurantSummaryType,
} from '~/features/guide/getRestaurantSummary.graphql'
import { RestaurantMenusFragment } from '~/features/guide/menus/query'
import type { Menus } from '~/features/guide/menus/query'
import { fetchOtoriyose } from '~/features/guide/otoriyose/fetch.server'
import { PCGuideRoot } from '~/features/guide/pc/root'
import { scope, useSetRestaurant } from '~/features/guide/scope'
import { SDGuideRoot } from '~/features/guide/sd/root'
import { RequestBeacon } from '~/features/guide/shared/RequestBeacon'
import { processHTML } from '~/features/kiwamino/lib/sanitize.server'
import { fetchQuery } from '~/graphql/fetchQuery'
import { guideCacheHeaders } from '~/headers'
import { todayAsDateText } from '~/lib/date/date'
import { differenceInSeconds } from '~/lib/date/dateTime'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import { validateRestaurantId } from '~/lib/graphql/scalars'
import type { HtmlFragment, RestaurantId } from '~/lib/graphql/scalars'
import { validateGuideQuery } from '~/lib/query/guide'
import { restaurantIdShouldRevalidate } from '~/lib/remix'
import { guideMeta } from '~/lib/seo/guide'
import { useIsPC } from '~/lib/ua'
import { useSetRestaurantContext } from '~/navigation'

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  return loaderHeaders
}

export async function loader({ params, request, context }: LoaderFunctionArgs) {
  // XXX(es2024): Promise.withResolvers
  const resolveMenus = (() => {
    let r: (v: Menus) => void
    // for $restaurantId.menus.tsx
    context['guide-menus-promise'] = new Promise((resolve) => {
      r = resolve
    })
    // @ts-expect-error: r is initialized in Promise constructor above
    return r
  })()
  const restaurantId = validateRestaurantId(params.restaurantId)
  if (!restaurantId) {
    throw new Response('Not Found', { status: 404 })
  }
  const queryClient = new QueryClient()
  const [summary] = await Promise.all([
    prefetchRestaurant(restaurantId, queryClient, request, resolveMenus),
    prefetchKiwaminoArticles(restaurantId, queryClient, request),
    prefetchOtoriyose(restaurantId, queryClient),
  ])
  if (!summary.restaurant) {
    throw new Response('Not Found', { status: 404 })
  }
  const [showIkyuDayBanner, maxAge, staleWhileRevalidate] = ikyuDayBannerShownAndCacheTime(summary)

  const url = new URL(request.url)
  // FIXME atom hydration で使える？
  const query = validateGuideQuery(url.searchParams, todayAsDateText())
  return json(
    {
      restaurantId,
      query,
      restaurant: summary.restaurant,
      showIkyuDayBanner,
      dehydratedState: dehydrate(queryClient),
    },
    { headers: guideCacheHeaders(summary.restaurant, { maxAge, staleWhileRevalidate }) }
  )
}

async function prefetchRestaurant(
  restaurantId: RestaurantId,
  queryClient: QueryClient,
  request: Request,
  resolveMenus: (menus: Menus) => void
) {
  const isIkyu = import.meta.env.VITE_MODE === 'ikyu'
  const data = await fetchQuery(GetRestaurantSummary, { restaurantId, isIkyu }, request)
  if (data.restaurant?.discountCoupons) {
    const coupons = getFragmentData(Coupon, data.restaurant.discountCoupons)
    queryClient.setQueryData(discountCouponsQueryKey(restaurantId), coupons)
  }
  if (data.restaurant) {
    const { menus } = getFragmentData(RestaurantMenusFragment, data.restaurant)
    if (menus) {
      resolveMenus(menus)
    }
  }
  return data
}

async function prefetchKiwaminoArticles(
  restaurantId: RestaurantId,
  queryClient: QueryClient,
  request: Request
) {
  if (import.meta.env.VITE_MODE === 'ppg') {
    return
  }
  return queryClient.prefetchQuery({
    queryKey: ['kiwamino-articles', restaurantId],
    queryFn: async () => {
      const articles = await fetchQuery(GetRestaurantArticle, { restaurantId }, request)
      return articles?.restaurant?.kiwaminoArticles?.map((fragment) => {
        const article = getFragmentData(KiwaminoArticleInfo, fragment)
        return { ...article, content: processHTML(article.content) as HtmlFragment }
      }) as FragmentType<typeof KiwaminoArticleInfo>[] | undefined
    },
  })
}

async function prefetchOtoriyose(restaurantId: RestaurantId, queryClient: QueryClient) {
  if (import.meta.env.VITE_MODE === 'ppg') {
    return
  }
  queryClient.prefetchQuery({
    queryKey: ['otoriyose-products', restaurantId],
    queryFn: () => fetchOtoriyose(restaurantId),
  })
}

// `/$restaurantId/reserve?xxx` から戻った場合に loader が走って欲しくないので restaurantId だけを見る
export const shouldRevalidate = restaurantIdShouldRevalidate

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (data === undefined) {
    throw new Response('Not Found', { status: 404 })
  }
  const { restaurantId, restaurant } = data
  return guideMeta(restaurantId, restaurant)
}

export default function Guide() {
  // loader から取得した Date 型は string 型にシリアライズされるため、props に渡す際に Date 型に戻す
  // https://github.com/remix-run/remix/discussions/634
  const { dehydratedState, ...props } = useLoaderData<typeof loader>()
  const { restaurantId, restaurant } = props

  useInitRestaurantId(restaurantId)

  // リストからの予約導線では context 経由でレストラン情報を受け取るので、ガイドランディング時も同じ context になるように無理矢理上書き
  // FIXME もっとスマートなやりかたはないだろうか...
  useSetRestaurantContext(restaurant)

  const isPC = useIsPC()

  return (
    <HydrationBoundary state={dehydratedState}>
      <RequestBeacon restaurantId={restaurantId} />
      <scope.Provider>
        <Init restaurant={restaurant} />
        {isPC ? <PCGuideRoot {...props} /> : <SDGuideRoot {...props} />}
      </scope.Provider>
    </HydrationBoundary>
  )
}

function Init(props: {
  restaurant: RestaurantSummaryType
}) {
  const setRestaurant = useSetRestaurant()
  useEffect(() => {
    setRestaurant(props.restaurant)
  }, [props.restaurant])
  return null
}

/**
 * 一休の日バナーを表示するかどうかと、キャッシュ時間 (max-age, stale-while-revalidate) を返す
 */
function ikyuDayBannerShownAndCacheTime(
  data: ResultOf<typeof GetRestaurantSummary>
): [boolean, number, number] {
  // 事前条件: data.restaurant は存在する
  if (!data.restaurant) {
    throw new Error('data.restaurant should exist')
  }
  const defaultMaxAge = 5 * 60
  const defaultStaleWhileRevalidate = 24 * 60 * 60
  if (data.restaurant.ikyuDaySetting) {
    const now = data.now
    const start = data.restaurant.ikyuDaySetting.reservablePeriod.from
    const secondsBeforeStart = differenceInSeconds(start, now)
    if (secondsBeforeStart > 0) {
      // 開始前なので非表示
      return [
        false,
        Math.min(secondsBeforeStart, defaultMaxAge),
        Math.min(secondsBeforeStart, defaultStaleWhileRevalidate),
      ]
    }
    const end = data.restaurant.ikyuDaySetting.reservablePeriod.to
    const secondsBeforeEnd = differenceInSeconds(end, now)
    if (secondsBeforeEnd > 0) {
      // 開催中なので表示
      return [
        true,
        Math.min(secondsBeforeEnd, defaultMaxAge),
        Math.min(secondsBeforeEnd, defaultStaleWhileRevalidate),
      ]
    }
  }
  // デフォルト
  return [false, defaultMaxAge, defaultStaleWhileRevalidate]
}
